import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Download, Play } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { libraryReturnPath } from '../utils/libraryNavigation';
import { normalizeNote, noteText } from '../utils/notePresentation';
import { stripSimpleMarkdown } from '../utils/simpleMarkdown';
import { getNoteThemeStyle, normalizeNoteTheme } from '../utils/noteTheme';
import usePageThemeColor from '../hooks/usePageThemeColor';
import apiClient from '../services/apiClient';
import PresetLibraryCoverImage from './PresetLibraryCoverImage';
import LibraryReviews from './LibraryReviews';
import NoteDetailHeader from './NoteDetailHeader';
import NoteCardList from './NoteCardList';
import QuickPracticeAction from './QuickPracticeAction';
import FavoriteNoteAction from './FavoriteNoteAction';
import { t } from '../i18n';
import './NoteDetail.css';

const previewText = value => stripSimpleMarkdown(value).replace(/\s+/g, ' ').trim();
function previewLine(card) {
  if (card.cardType === 'reading') return previewText(card.readingTitle || card.front || card.title) || t('library.readingCard');
  return [previewText(card.back), previewText(card.front)].filter(Boolean).join(' / ') || '—';
}

function PresetDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { presetId } = useParams();
  const [preset, setPreset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const importOperation = useRef(null);
  const back = () => navigate(libraryReturnPath(location.state, '/library'));
  usePageThemeColor(normalizeNoteTheme(preset || {}));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPreset(null);
    setError('');
    importOperation.current = null;
    apiClient.getPresetLibraryItem(presetId)
      .then(data => { if (!cancelled) setPreset(data); })
      .catch(err => { if (!cancelled) setError(err.message || noteText('notFound')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [presetId]);

  const handleImport = async () => {
    if (!preset || importing) return;
    if (preset.installed) return;
    setImporting(true);
    setError('');
    try {
      importOperation.current ||= uuid();
      const isVocabulary = preset.studyKind === 'vocabulary' || preset.category === 'Vocabulary';
      const result = await apiClient.importPresetLibraryItem(preset.id, ...(isVocabulary ? [{ operationId: importOperation.current }] : []));
      setPreset(current => ({ ...current, installed: true, installedNoteId: result.noteId }));
      navigate(isVocabulary ? '/vocabulary' : '/notes', { state: { importedNote: { id: result.noteId, title: preset.title } } });
    } catch (err) {
      if (err.status === 409) {
        setPreset(current => ({ ...current, installed: true, installedNoteId: err.data?.error?.installedNoteId || null }));
      } else setError(err.message || noteText('notFound'));
    } finally { setImporting(false); }
  };

  if (loading) return <div role="status">{noteText('loading')}</div>;
  if (!preset) return <div role="alert"><p>{error || noteText('notFound')}</p><button onClick={back}>Library</button></div>;
  const note = normalizeNote(preset, 'preset');
  const useTextPreview = Number(note.cardCount) > 150;
  const noteThemeStyle = getNoteThemeStyle(note);
  const importLabel = noteText(preset.installed ? 'added' : 'addToNotes');
  return (
    <div className={`note-detail-page${noteThemeStyle ? ' has-note-theme' : ''}`} style={noteThemeStyle}>
      <NoteDetailHeader note={note} backLabel={t('dashboard.library')} onBack={back}
        primaryActionClassName="has-learning-actions"
        primaryAction={<div className="note-detail-learning-actions">
          <FavoriteNoteAction sourceType="official" sourceId={preset.id} initialFavorite={Boolean(preset.isFavorite)} />
          {preset.studyKind !== 'vocabulary' && preset.category !== 'Vocabulary' && <QuickPracticeAction source={{ type: 'preset', id: preset.id }} note={note} disabled={!note.cards.length}
            renderTrigger={(open, busy) => <button type="button" className="note-detail-button note-detail-button-primary" disabled={busy || !note.cards.length} onClick={open}>
              <Play size={18} fill="currentColor" aria-hidden="true" />{noteText('quickPractice')}
            </button>} />}
          <button type="button" className="note-detail-button note-detail-round-action" aria-label={importLabel} title={importLabel}
            disabled={importing || preset.installed} onClick={handleImport}>
            <Download size={20} aria-hidden="true" />
          </button>
        </div>}
        cover={<PresetLibraryCoverImage presetId={preset.id} hasCover={Boolean(preset.coverUrl)} alt={note.title} />}>
        {(preset.recommendedFor || []).length > 0 && <div><h2>{noteText('recommended')}</h2><ul>{preset.recommendedFor.map(value => <li key={value}>{value}</li>)}</ul></div>}
        {error && <p className="note-detail-error" role="alert">{error}</p>}
      </NoteDetailHeader>
      {useTextPreview || !note.cards.length ? <section className="note-detail-cards">
        <header><h2>{t('library.presetPreviewTitle')}</h2></header>
        {note.cards.length ? <ol className="note-text-preview" aria-label={t('library.presetPreviewTitle')}>
          {note.cards.map(card => <li key={card.id}>{previewLine(card)}</li>)}
        </ol> : <p className="note-text-preview-empty">{t('library.presetPreviewEmpty')}</p>}
      </section> : <NoteCardList cards={note.cards} preview title={t('library.presetPreviewTitle')} description={t('library.presetPreviewHelp')} paginationLabel={t('library.previewPagination')} />}
      <LibraryReviews canReview={Boolean(preset.installed)}
        loadReviews={page => apiClient.getPresetLibraryReviews(preset.id, { page })}
        saveReview={review => apiClient.savePresetLibraryReview(preset.id, review)} />
    </div>
  );
}

export default PresetDetailsPage;
